import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Mail,
  Pencil,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  useAgenticApprovalRun,
  type ApprovalRow,
} from "./useAgenticApprovalRun";

// Sprint 10.x — Bouton 1-clic Today Théo : pour chaque inscrit récent
// plateforme jamais contacté, l'agent rédige un mail perso (PostHog activity)
// et le pousse dans la queue d'approvals. Théo valide / refuse chaque mail.

type Candidate = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  age_days: number;
};

type SkillApproval = {
  contact_id: number;
  contact_label: string;
  approval_id: string;
  subject: string;
};

const RELANCE_LIMIT = 10;

function fullName(c: Candidate) {
  return (
    `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || `Contact #${c.id}`
  );
}

const extractBody = (payload: Record<string, unknown>) => {
  const toList = payload.to as string[] | undefined;
  return {
    body: (payload.text_body as string | undefined) ?? "(corps vide)",
    recipient: Array.isArray(toList) ? toList.join(", ") : undefined,
  };
};

const applyEdits = (
  payload: Record<string, unknown>,
  subject: string,
  text_body: string,
) => ({ ...payload, subject, text_body });

export const RelanceNewSignupsCard = () => {
  const notify = useNotify();
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const {
    runState,
    progress,
    approvals,
    runError,
    patch,
    launch: launchRun,
    toggleBody,
    startEdit,
    cancelEdit,
    updateEdit,
    saveEdit: saveEditRow,
    decide,
  } = useAgenticApprovalRun({
    extractBody,
    applyEdits,
    approveSuccessLabel: "Email envoyé",
    doneLabel: "relance(s) à valider",
  });

  const reload = useCallback(async () => {
    setLoadErr(null);
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from("relance_candidates")
      .select("id, first_name, last_name, company_name, age_days")
      .limit(RELANCE_LIMIT);
    if (error) {
      setLoadErr(error.message);
      setCandidates([]);
      return;
    }
    setCandidates((data ?? []) as Candidate[]);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const launch = () =>
    launchRun(
      "relance_new_signups",
      { limit: RELANCE_LIMIT },
      (out) =>
        ((out.approvals as SkillApproval[] | undefined) ?? []).map((a) => ({
          approval_id: a.approval_id,
          contact_label: a.contact_label,
          subtitle: a.subject,
          state: "pending" as const,
        })),
      async () => {
        // Annule les drafts pending du run précédent (sinon ils restent
        // en file et bloquent les contacts dans la vue relance_candidates).
        const { data: cancelled } = await getSupabaseClient().rpc(
          "cancel_pending_relance_approvals",
        );
        if (typeof cancelled === "number" && cancelled > 0) {
          notify(`${cancelled} draft(s) précédent(s) annulé(s)`, {
            type: "info",
          });
          await reload();
        }
      },
    );

  const saveEdit = (a: ApprovalRow) =>
    (a.editedSubtitle ?? "").trim()
      ? saveEditRow(a)
      : patch(a.approval_id, { saveError: "Le sujet ne peut pas être vide." });

  return (
    <Card>
      <CardContent className="py-4 md:py-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="size-4 text-fabrik-violet" />
          <p className="text-[13px] font-semibold text-foreground">
            Relance IA — nouveaux inscrits
          </p>
          {candidates && candidates.length > 0 && (
            <span className="text-[11px] text-muted-foreground">
              ({candidates.length})
            </span>
          )}
        </div>

        {candidates === null ? (
          <Skeleton className="h-12 w-full" />
        ) : loadErr ? (
          <p className="text-xs text-destructive">{loadErr}</p>
        ) : candidates.length === 0 ? (
          <p className="text-xs text-muted-foreground py-1">
            Aucun nouvel inscrit à relancer (déjà contactés ou abonnés).
          </p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-2">
              {candidates.length} inscrit(s) plateforme &lt; 7j sans contact
              humain. L'IA propose un mail perso pour chacun.
            </p>
            <ul className="text-xs text-muted-foreground mb-3 space-y-0.5 max-h-24 overflow-auto">
              {candidates.slice(0, 5).map((c) => (
                <li key={c.id} className="truncate">
                  <Link
                    to={`/contacts/${c.id}/show`}
                    className="hover:underline text-foreground"
                  >
                    {fullName(c)}
                  </Link>
                  {c.company_name ? ` · ${c.company_name}` : ""}
                  <span className="text-fabrik-text-3">
                    {" "}
                    · il y a {Math.max(1, Math.round(c.age_days))}j
                  </span>
                </li>
              ))}
              {candidates.length > 5 && (
                <li className="text-fabrik-text-3">
                  … +{candidates.length - 5} autres
                </li>
              )}
            </ul>

            {runState === "idle" && (
              <Button size="sm" onClick={launch} className="w-full">
                <Mail className="size-3.5 mr-1.5" />
                Lancer la relance IA ({candidates.length})
              </Button>
            )}
            {runState === "running" && (
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-2">
                <Loader2 className="size-3.5 animate-spin" />
                Génération en cours… {progress}/{candidates.length}
              </div>
            )}
            {runState === "error" && (
              <div className="text-xs text-destructive py-2">
                Erreur : {runError}
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-2"
                  onClick={launch}
                >
                  Réessayer
                </Button>
              </div>
            )}
          </>
        )}

        {approvals.length > 0 && (
          <div className="mt-4 border-t pt-3 space-y-2">
            <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 flex items-center gap-1">
              <ShieldCheck className="size-3" />
              {approvals.length} mail(s) à valider
            </div>
            {approvals.map((a) => (
              <div
                key={a.approval_id}
                className="rounded border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10 p-2"
              >
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={() => toggleBody(a)}
                    className="flex-1 min-w-0 text-left flex items-start gap-1.5 hover:bg-amber-500/10 rounded px-1 -mx-1 -my-0.5 py-0.5"
                    title={a.expanded ? "Replier" : "Voir le mail"}
                  >
                    {a.expanded ? (
                      <ChevronDown className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">
                        {a.contact_label}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {a.subtitle}
                      </div>
                    </div>
                  </button>
                  {a.state === "pending" && !a.editing && (
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={async () => {
                          // S'assure que le body est chargé avant édition
                          if (!a.body && !a.bodyLoading) await toggleBody(a);
                          startEdit(a);
                        }}
                        title="Modifier le mail"
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={() => decide(a, "reject")}
                      >
                        <X className="size-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => decide(a, "execute")}
                      >
                        <Check className="size-3.5" />
                      </Button>
                    </div>
                  )}
                  {a.editing && (
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => cancelEdit(a)}
                        disabled={a.saving}
                      >
                        Annuler
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => saveEdit(a)}
                        disabled={a.saving}
                      >
                        {a.saving ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          "Enregistrer"
                        )}
                      </Button>
                    </div>
                  )}
                  {(a.state === "applying" || a.state === "rejecting") && (
                    <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                  )}
                  {a.state === "done" && (
                    <span className="text-[11px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1 shrink-0">
                      <Check className="size-3" /> Envoyé
                    </span>
                  )}
                  {a.state === "rejected" && (
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      Refusé
                    </span>
                  )}
                  {a.state === "error" && (
                    <span
                      className="text-[11px] text-destructive shrink-0"
                      title={a.error}
                    >
                      Erreur
                    </span>
                  )}
                </div>
                {a.expanded && (
                  <div className="mt-2 pt-2 border-t border-amber-500/20 text-xs">
                    {a.bodyLoading ? (
                      <div className="flex items-center gap-1.5 text-muted-foreground py-1">
                        <Loader2 className="size-3 animate-spin" />
                        Chargement…
                      </div>
                    ) : a.bodyError ? (
                      <div className="text-destructive">{a.bodyError}</div>
                    ) : a.editing ? (
                      <div className="space-y-2">
                        {a.recipient && (
                          <div className="text-[11px] text-muted-foreground">
                            À : {a.recipient}
                          </div>
                        )}
                        <div>
                          <label className="text-[10.5px] uppercase tracking-wider text-muted-foreground block mb-1">
                            Sujet
                          </label>
                          <input
                            type="text"
                            value={a.editedSubtitle ?? ""}
                            onChange={(e) =>
                              updateEdit(
                                a.approval_id,
                                "subtitle",
                                e.target.value,
                              )
                            }
                            className="w-full text-xs px-2 py-1.5 rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-fabrik-violet"
                            disabled={a.saving}
                          />
                        </div>
                        <div>
                          <label className="text-[10.5px] uppercase tracking-wider text-muted-foreground block mb-1">
                            Corps
                          </label>
                          <textarea
                            value={a.editedBody ?? ""}
                            onChange={(e) =>
                              updateEdit(a.approval_id, "body", e.target.value)
                            }
                            rows={10}
                            className="w-full text-xs px-2 py-1.5 rounded border border-border bg-background font-mono focus:outline-none focus:ring-1 focus:ring-fabrik-violet"
                            disabled={a.saving}
                          />
                        </div>
                        {a.saveError && (
                          <div className="text-destructive text-[11px]">
                            {a.saveError}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {a.recipient && (
                          <div className="text-[11px] text-muted-foreground mb-1">
                            À : {a.recipient}
                          </div>
                        )}
                        <pre className="whitespace-pre-wrap font-sans text-foreground leading-relaxed">
                          {a.body}
                        </pre>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
